import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DiscountCouponService from "./Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import Select from 'react-select';
import { Col, CardBody, Card } from "reactstrap";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { uploadFile } from "../../utils/fileUtils";

const AddDiscountCouponModal = ({ showModalAddDiscountCoupon, handleToggleModalAddDiscountCoupon, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();

  const [data, setData] = useState({
    title: '',
    sub_title: '',
    features: '',
    price: '',
    description: '',
    image: '',
  });

  const [errors, setErrors] = useState({
    title: '',
    sub_title: '',
    features: '',
    price: '',
    description: '',
    image: '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    let error = '';

    if (type === 'checkbox') {
      // Handle checkboxes for features
      let featuresArray = data.features ? data.features.split(',') : [];
      if (checked) {
        featuresArray.push(value);
      } else {
        featuresArray = featuresArray.filter((feature) => feature !== value);
      }
      setData(prevData => ({ ...prevData, features: featuresArray.join(',') }));

      // Remove error if at least one checkbox is checked
      if (featuresArray.length > 0) {
        setErrors(prevErrors => ({ ...prevErrors, features: '' }));
      }
    } else if (name === 'image' && files && files.length > 0) {
      const file = files[0];
      setLoading(true);
      uploadFile(file)
        .then((path) => {
          setData((prevData) => ({ ...prevData, image: path }));
          setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
        })
        .catch((error) => {
          Alerts.swalErrorAlert(error.message || t("Image upload failed."));
        })
        .finally(() => setLoading(false));
    } else {
      if (name === 'title' && !value) {
        error = 'Title is required';
      } else if (name === 'sub_title' && !value) {
        error = 'Sub title is required';
      } else if (name === 'price' && !value) {
        error = 'Price is required';
      } else if (name === 'description' && !value) {
        error = 'Description is required';
      }
      setData(prevData => ({ ...prevData, [name]: value }));
    }

    setErrors(prevErrors => ({ ...prevErrors, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let validationErrors = {};

    if (!data.title) { validationErrors.title = 'Title is required'; }
    if (!data.sub_title) { validationErrors.sub_title = 'Sub title is required'; }
    if (!data.features) { validationErrors.features = 'Feature is required'; }
    if (!data.price) { validationErrors.price = 'Price is required'; }
    if (!data.description) { validationErrors.description = 'Description is required'; }
    if (!data.image) { validationErrors.image = 'Image is required'; }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      DiscountCouponService.AddDevice(authData.sessionID, data).then((response) => {
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          if (response.data.status === true) {
            resetForm();
            handleToggleModalAddDiscountCoupon();
            onSuccess();
            validationErrors.name = '';
            Alerts.swalSuccessAlert(t("Device added successfully."));
          } else {
            validationErrors.name = response.data.errors[0].message || 'An error occurred';
            setErrors(validationErrors);
          }
        }
      }, (error) => {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      });
    }
  };

  const resetForm = () => {
    setData({
      title: '',
      sub_title: '',
      features: '',
      price: '',
      description: '',
      image: '',
    });
    document.querySelectorAll(".form-check-input").forEach(input => input.checked = false);
  };

  const handleClose = () => {
    setErrors({
      title: '',
      sub_title: '',
      features: '',
      price: '',
      description: '',
      image: '',
    });
    resetForm();
    handleToggleModalAddDiscountCoupon();
  };

  return (
    <div className={showModalAddDiscountCoupon ? "modal d-block mt-4" : "modal"} style={{ display: showModalAddDiscountCoupon ? 'block' : 'none', backgroundColor: 'rgba(0, 0, 0, 0.5)' }} tabIndex="-1" role="dialog">
      <form onSubmit={handleSubmit}>
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">{t("Add Device")}</h5>
            </div>
            <div className="modal-body">
              <Col md="12">
                <Card className="main-card mb-3">
                  <CardBody>
                    <LoadingOverlay
                      tag="div"
                      active={loading}
                      styles={{ overlay: (base) => ({ ...base }) }}
                      spinner={<DNALoader />}
                    >
                      <input type="hidden" className="form-control" name="business_id" />

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="coupon-code" className="col-form-label">{t("Title")}:</label>
                            <input type="text" className="form-control" placeholder="Enter Title"
                              name="title"
                              onChange={handleChange}
                              value={data.title}
                            />
                            {errors.title && <strong className="error text-danger">{errors.title}</strong>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="coupon-code" className="col-form-label">{t("Sub Title")}:</label>
                            <input type="text" className="form-control" placeholder="Enter Sub Title"
                              name="sub_title"
                              onChange={handleChange}
                              value={data.sub_title}
                            />
                            {errors.sub_title && <strong className="error text-danger">{errors.sub_title}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="coupon-code" className="col-form-label">{t("Price")}:</label>
                            <input type="number" className="form-control" placeholder="Enter Price"
                              name="price"
                              onChange={handleChange}
                              value={data.price}
                            />
                            {errors.price && <strong className="error text-danger">{errors.price}</strong>}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="coupon-code" className="col-form-label">{t("Image")}:</label>
                            <input type="file" className="form-control"
                              name="image"
                              onChange={handleChange}
                            />
                            {errors.image && <strong className="error text-danger">{errors.image}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="coupon-code" className="col-form-label">{t("Features")}:</label>
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" value="Mobile App" id="feature1" onChange={handleChange} />
                              <label className="form-check-label" htmlFor="feature1">Mobile App</label>
                            </div>
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" value="Bluetooth" id="feature2" onChange={handleChange} />
                              <label className="form-check-label" htmlFor="feature2">Bluetooth</label>
                            </div>
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" value="Rechargeable" id="feature3" onChange={handleChange} />
                              <label className="form-check-label" htmlFor="feature3">Rechargeable</label>
                            </div>
                            {errors.features && <strong className="error text-danger">{errors.features}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="coupon-code" className="col-form-label">{t("Description")}:</label>
                            <textarea className="form-control" style={{ height: '120px' }} placeholder="Enter Description"
                              name="description"
                              onChange={handleChange}
                              value={data.description}
                            />
                            {errors.description && <strong className="error text-danger">{errors.description}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="text-center mt-4">
                          <button type="button" className="btn btn-outline-light btn-hover-shine me-3" onClick={handleClose}>{t("Close")}</button>
                          <button type="submit" className="btn btn-custom-color">{t("Save")}</button>
                        </div>
                      </div>
                    </LoadingOverlay>
                  </CardBody>
                </Card>
              </Col>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddDiscountCouponModal;
